import classNames from 'classnames/bind';
import styles from './ContentSection.module.css';
import { motion, Variants } from 'framer-motion';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { useRouter } from 'next/router';
import { Button } from 'antd';
import LINK, { LOCALE_LINK } from 'constants/link';
import { getLocaleClassNames, toLocale } from 'utils/locale';

const cx = classNames.bind(styles);

const cardVariants: Variants = {
  offscreen: {
    opacity: 0,
  },
  onscreen: {
    opacity: 1,
    transition: {
      type: 'spring',
      bounce: 0.1,
      duration: 1,
    },
  },
};

function ContentSection() {
  const { t } = useTranslation('business');
  const router = useRouter();
  const { locale } = router;
  const localeCx = (className: string) =>
    cx(getLocaleClassNames({ className, locale }));

  return (
    <>
      <motion.div
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true }}
        id="content"
        className={cx('content-container', 'blue-wrapper')}
      >
        <motion.div variants={cardVariants} className={cx('content-wrapper')}>
          <h1>{t('content')}</h1>
          <h2>
            <Trans
              i18nKey={t('content-description')}
              components={{
                br: <br />,
              }}
            />
          </h2>
        </motion.div>
      </motion.div>
      <motion.div
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true }}
      >
        <motion.div className={cx('main-wrapper')} variants={cardVariants}>
          <div className={cx('various-box', 'type-01')}>
            <div className={cx('content-left')}>
              <div className={cx('left-image')} />
            </div>
            <div className={cx('content-right')}>
              <div className={cx('mobile-left-image-container')}>
                <div className={localeCx('right-image-01')} />
              </div>
              <div className={cx('mobile-right-image-container')}>
                <div className={cx('right-image-02')} />
              </div>
            </div>
          </div>
          <div className={cx('various-box', 'type-01', 'text-container')}>
            <div className={cx('content-left')}>
              <h1>{t('youtube')}</h1>
              <p>
                <Trans
                  i18nKey={t('youtube-description')}
                  components={{
                    br: (
                      <br className="disable-mobile-break disable-tablet-break" />
                    ),
                  }}
                />
              </p>
            </div>
            <div className={cx('content-right')} />
          </div>
        </motion.div>
      </motion.div>

      {/* 영화 및 애니메이션 PC 전용 UI */}
      <motion.div
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true }}
      >
        <motion.div variants={cardVariants} className={cx('blue-wrapper')}>
          <div className={cx('various-box', 'type-02')}>
            <div className={cx('content-left')}>
              <div className={localeCx('left-image-01')} />
              <div className={localeCx('left-image-02')} />
            </div>
            <div className={cx('content-right')}>
              <div className={localeCx('right-image-01')} />
              <div className={localeCx('right-image-02')} />
              <h1>{t('movies-and-animated-series')}</h1>
              <div>
                <h4>
                  <Trans
                    i18nKey={t('baby-shark-movie-version')}
                    components={{
                      br: <br />,
                    }}
                  />
                </h4>
                <p>
                  <Trans
                    i18nKey={t('baby-shark-movie-version-description')}
                    components={{
                      br: (
                        <br className="disable-mobile-break disable-tablet-break" />
                      ),
                    }}
                  />
                </p>
              </div>
              <div>
                <h4>{t('baby-shark-brooklyn')}</h4>
                <p>
                  <Trans
                    i18nKey={t('baby-shark-brooklyn-description')}
                    components={{
                      br: <br />,
                    }}
                  />
                </p>
              </div>
              <div>
                <h4>{t('pinkfong-and-baby-shark-space-adventure')}</h4>
                <p>
                  <Trans
                    i18nKey={t(
                      'pinkfong-and-baby-shark-space-adventure-description'
                    )}
                    components={{
                      br: (
                        <br className="disable-mobile-break disable-tablet-break" />
                      ),
                    }}
                  />
                </p>
              </div>
              <div>
                <h4>{t('bebefinn')}</h4>
                <p>
                  <Trans
                    i18nKey={t('bebefinn-description')}
                    components={{
                      br: <br />,
                    }}
                  />
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
      {/* 영화 및 애니메이션 모바일 전용 UI */}
      <motion.div
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true }}
      >
        <motion.div variants={cardVariants} className={cx('blue-wrapper')}>
          <div className={cx('mobile-various-box', 'type-02')}>
            <div className={localeCx('image-01')} />
            <div className={localeCx('image-02')} />
            <div className={localeCx('image-03')} />
            <div className={localeCx('image-04')} />
            <h1>{t('movies-and-animated-series')}</h1>
            <div>
              <h4>
                <Trans
                  i18nKey={t('baby-shark-movie-version')}
                  components={{
                    br: <br />,
                  }}
                />
              </h4>
              <p>
                <Trans
                  i18nKey={t('baby-shark-movie-version-description')}
                  components={{
                    br: (
                      <br className="disable-mobile-break disable-tablet-break" />
                    ),
                  }}
                />
              </p>
            </div>
            <div>
              <h4>{t('baby-shark-brooklyn')}</h4>
              <p>
                <Trans
                  i18nKey={t('baby-shark-brooklyn-description')}
                  components={{
                    br: <br />,
                  }}
                />
              </p>
            </div>
            <div>
              <h4>{t('pinkfong-and-baby-shark-space-adventure')}</h4>
              <p>
                <Trans
                  i18nKey={t(
                    'pinkfong-and-baby-shark-space-adventure-description'
                  )}
                  components={{
                    br: (
                      <br className="disable-mobile-break disable-tablet-break" />
                    ),
                  }}
                />
              </p>
            </div>
            <div>
              <h4>{t('bebefinn')}</h4>
              <p>
                <Trans
                  i18nKey={t('bebefinn-description')}
                  components={{
                    br: <br />,
                  }}
                />
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true }}
      >
        <motion.div variants={cardVariants} className={cx('main-wrapper')}>
          <div className={cx('various-box', 'type-03')}>
            <div className={cx('content-left')}>
              <div className={localeCx('left-image')} />
            </div>
            <div className={cx('content-right')}>
              <div className={cx('mobile-left-image-container')}>
                <div className={cx('right-image-01')} />
              </div>
              <div className={cx('mobile-right-image-container')}>
                <div className={cx('right-image-02')} />
              </div>
            </div>
          </div>
          <div className={cx('various-box', 'type-03', 'text-container')}>
            <div className={cx('content-left')}>
              <h1>{t('music')}</h1>
              <p>
                <Trans
                  i18nKey={t('music-description')}
                  components={{
                    br: <br className="disable-mobile-break" />,
                  }}
                />
              </p>
            </div>
            <div className={cx('content-right')} />
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true }}
      >
        <motion.div variants={cardVariants} className={cx('blue-wrapper')}>
          <div className={cx('various-box', 'type-04')}>
            <div className={cx('content-left')}>
              <div className={cx('left-image')} />
            </div>
            <div className={cx('content-right')}>
              <div className={cx('image-container')}>
                <div className={cx('mobile-left-image-container')}>
                  <div className={cx('right-image-01')} />
                </div>
                <div className={cx('mobile-right-image-container')}>
                  <div className={cx('right-image-02')} />
                </div>
              </div>
              <h1>{t('live-events')}</h1>
              <p>
                <Trans
                  i18nKey={t('live-events-description')}
                  components={{
                    br: <br />,
                  }}
                />
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true }}
      >
        <motion.div variants={cardVariants} className={cx('main-wrapper')}>
          <div className={cx('various-box', 'type-05')}>
            <div className={localeCx('image')} />
            <div className={cx('content')}>
              <h1>{t('app')}</h1>
              <p>
                <Trans
                  i18nKey={t('app-description')}
                  components={{
                    br: <br className="disable-mobile-break" />,
                  }}
                />
              </p>
            </div>
          </div>
          <Button type="primary" shape="round">
            <a
              href={LOCALE_LINK['pinkfong-plus'][toLocale(locale)]}
              target="_blank"
              rel="noreferrer"
            >
              <Trans i18nKey={t('more-about-pinkfong-plus')} />
            </a>
          </Button>
        </motion.div>
      </motion.div>
    </>
  );
}

export default ContentSection;
